import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import { postDataAPI } from '../../utils/fetchData';
import { REMINDER_TEMPLATES } from '../../utils/remindersData';
import { CustomCalendar } from '../../components/Calendar';
import { getTeamMembersForTasks } from '../../utils/approvalUtils';

const CreateCustomWorkflow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userArticle } = useSelector((state) => state.auth);
  
  // Determine initial workflow type based on route
  const getInitialWorkflowType = () => {
    if (location.pathname.includes('/song-release')) return 'song_release';
    if (location.pathname.includes('/upcoming-show')) return 'upcoming_show';
    if (location.pathname.includes('/create-workflow')) return 'custom';
    return '';
  };
  
  const [workflowType, setWorkflowType] = useState(getInitialWorkflowType()); // song_release, upcoming_show, custom
  const [formData, setFormData] = useState({
    title: '',
    songName: '',
    showName: '',
    description: '',
    releaseDate: '',
    performanceDate: '',
    assignee: ''
  });
  
  const [steps, setSteps] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [disableFollowUps, setDisableFollowUps] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Fetch team members on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoadingTeam(true);
    try {
      // Use the same logic as TasksApp for consistency
      const members = await getTeamMembersForTasks();
      
      // Convert to the format expected by this component
      const formattedMembers = members.map(member => ({
        id: member._id,
        name: member.name,
        role: member.role,
        email: member.email,
        _id: member._id
      }));
      
      setTeamMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
      // Fallback to just current user
      const currentUser = {
        id: userArticle?._id || 'self',
        name: `${userArticle?.username || userArticle?.email || 'Me'} (me)`,
        role: userArticle?.professionRole || 'Self',
        email: userArticle?.email,
        _id: userArticle?._id
      };
      setTeamMembers([currentUser]);
    } finally {
      setLoadingTeam(false);
    }
  };

  // Load preset steps when workflow type changes
  useEffect(() => {
    if (workflowType === 'song_release') {
      // Load song release workflow steps
      const releaseSteps = [
        { id: 'FIVE_WEEKS_OUT', ...REMINDER_TEMPLATES.FIVE_WEEKS_OUT, enabled: true },
        { id: 'FOUR_WEEKS_OUT', ...REMINDER_TEMPLATES.FOUR_WEEKS_OUT, enabled: true },
        { id: 'THREE_WEEKS_OUT', ...REMINDER_TEMPLATES.THREE_WEEKS_OUT, enabled: true },
        { id: 'TWO_WEEKS_OUT', ...REMINDER_TEMPLATES.TWO_WEEKS_OUT, enabled: true },
        { id: 'ONE_WEEK_OUT', ...REMINDER_TEMPLATES.ONE_WEEK_OUT, enabled: true },
        { id: 'FORTY_EIGHT_HOURS_OUT', ...REMINDER_TEMPLATES.FORTY_EIGHT_HOURS_OUT, enabled: true },
        { id: 'RELEASE_DAY', ...REMINDER_TEMPLATES.RELEASE_DAY, enabled: true },
        { id: 'SEVEN_DAYS_POST', ...REMINDER_TEMPLATES.SEVEN_DAYS_POST, enabled: true },
        { id: 'FOURTEEN_DAYS_POST', ...REMINDER_TEMPLATES.FOURTEEN_DAYS_POST, enabled: true },
      ];
      setSteps(releaseSteps);
    } else if (workflowType === 'upcoming_show') {
      // Load show workflow steps
      const showSteps = [
        { id: 'BOOKING_CONFIRMED', ...REMINDER_TEMPLATES.BOOKING_CONFIRMED, enabled: true },
        { id: 'BOOKING_CONTRACTING', ...REMINDER_TEMPLATES.BOOKING_CONTRACTING, enabled: true },
        { id: 'BOOKING_MARKETING_LAUNCH', ...REMINDER_TEMPLATES.BOOKING_MARKETING_LAUNCH, enabled: true },
        { id: 'BOOKING_SHOW_ADVANCE', ...REMINDER_TEMPLATES.BOOKING_SHOW_ADVANCE, enabled: true },
        { id: 'BOOKING_TICKET_COUNTS', ...REMINDER_TEMPLATES.BOOKING_TICKET_COUNTS, enabled: true },
        { id: 'BOOKING_TRAVEL_FINALIZATION', ...REMINDER_TEMPLATES.BOOKING_TRAVEL_FINALIZATION, enabled: true },
        { id: 'BOOKING_GUEST_LIST', ...REMINDER_TEMPLATES.BOOKING_GUEST_LIST, enabled: true },
        { id: 'BOOKING_POST_SHOW_SETTLEMENT', ...REMINDER_TEMPLATES.BOOKING_POST_SHOW_SETTLEMENT, enabled: true },
      ];
      setSteps(showSteps);
    } else if (workflowType === 'custom') {
      // Start with 3 empty custom steps
      setSteps([
        { id: 'custom_1', title: '', description: '', daysOffset: '30', enabled: true },
        { id: 'custom_2', title: '', description: '', daysOffset: '14', enabled: true },
        { id: 'custom_3', title: '', description: '', daysOffset: '1', enabled: true },
      ]);
    }
  }, [workflowType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleStepEnabled = (stepId) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, enabled: !step.enabled } : step
    ));
  };

  const updateCustomStep = (stepId, field, value) => {
    setSteps(prev => prev.map(step => 
      step.id === stepId ? { ...step, [field]: value } : step
    ));
  };

  const addCustomStep = () => {
    const newStep = {
      id: `custom_${Date.now()}`,
      title: '',
      description: '',
      daysOffset: '7',
      enabled: true
    };
    setSteps(prev => [...prev, newStep]);
  };

  const removeCustomStep = (stepId) => {
    setSteps(prev => prev.filter(step => step.id !== stepId));
  };

  const handleCreate = async () => {
    setIsCreating(true);
    
    try {
      // Validation
      if (!workflowType) {
        dispatch(setErrorAlert('Please select a workflow type'));
        setIsCreating(false);
        return;
      }

      if (workflowType === 'song_release' && !formData.songName) {
        dispatch(setErrorAlert('Please enter a song name'));
        setIsCreating(false);
        return;
      }

      if (workflowType === 'song_release' && !formData.releaseDate) {
        dispatch(setErrorAlert('Please select a release date'));
        setIsCreating(false);
        return;
      }

      if (workflowType === 'upcoming_show' && !formData.showName) {
        dispatch(setErrorAlert('Please enter a show name'));
        setIsCreating(false);
        return;
      }

      if (workflowType === 'upcoming_show' && !formData.performanceDate) {
        dispatch(setErrorAlert('Please select a performance date'));
        setIsCreating(false);
        return;
      }

      if (workflowType === 'custom') {
        if (!formData.title) {
          dispatch(setErrorAlert('Please enter a workflow title'));
          setIsCreating(false);
          return;
        }
        if (!formData.releaseDate && !formData.performanceDate) {
          dispatch(setErrorAlert('Please select a date'));
          setIsCreating(false);
          return;
        }
        // Validate custom steps have titles
        const invalidSteps = steps.filter(s => s.enabled && !s.title);
        if (invalidSteps.length > 0) {
          dispatch(setErrorAlert('Please add titles to all enabled steps'));
          setIsCreating(false);
          return;
        }
      }

      // Create the workflow
      const workflowId = `workflow_${Date.now()}`;
      const workflowName = workflowType === 'song_release' 
        ? formData.songName
        : workflowType === 'upcoming_show'
        ? formData.showName
        : formData.title;
      
      // Ensure workflowDate is in YYYY-MM-DD format
      let workflowDate = workflowType === 'song_release' 
        ? formData.releaseDate
        : workflowType === 'upcoming_show'
        ? formData.performanceDate
        : (formData.performanceDate || formData.releaseDate);
      
      // If workflowDate is an ISO string, extract just the date part
      if (workflowDate && workflowDate.includes('T')) {
        workflowDate = workflowDate.split('T')[0];
      }
      
      // Validate that we have a valid date
      if (!workflowDate) {
        dispatch(setErrorAlert('Please select a valid date'));
        setIsCreating(false);
        return;
      }

      const selectedMember = teamMembers.find(member => member.id === formData.assignee);
      const metadata = {
        type: workflowType === 'upcoming_show' ? 'booking' : 'release',
        description: formData.description,
        custom: workflowType === 'custom',
        assignee_name: selectedMember?.name,
        assignee_role: selectedMember?.role,
        assignee_email: selectedMember?.email,
        disableFollowUps: disableFollowUps
      };

      console.log('🔍 Creating workflow:', {
        workflowId,
        workflowName,
        workflowDate,
        workflowType,
        metadata
      });

      // Create workflow via API
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const enabledSteps = steps.filter(s => s.enabled);
      
      if (workflowType === 'song_release') {
        // Create song release workflow
        const response = await postDataAPI('user/reminders/create', {
          releaseId: workflowId,
          releaseName: workflowName,
          releaseDate: workflowDate
        }, token);
        
        if (response?.status !== 201 && !response?.data?.success) {
          console.error('❌ Song release backend response:', response);
          throw new Error(response?.data?.message || 'Failed to create song release workflow');
        }
        
        console.log('✅ Created song release workflow:', response.data);
      } else if (workflowType === 'upcoming_show') {
        // Create show workflow using the workflow endpoint
        const workflowSteps = enabledSteps.map((step, index) => ({
          title: step.title || `Show Step ${index + 1}`,
          description: step.description || `Step ${index + 1} for ${workflowName}`,
          timing: step.daysOffset || 'day_of',
          time: step.time || '10am',
          enabled: true,
          priority: 'medium'
        }));
        
        const response = await postDataAPI('user/reminders/workflow', {
          workflowName,
          workflowDate,
          workflowType: 'upcoming-show',
          steps: workflowSteps,
          assignee_name: selectedMember?.name,
          assignee_role: selectedMember?.role,
          assignee_email: selectedMember?.email
        }, token);
        
        if (response?.status !== 201 && !response?.data?.success) {
          console.error('❌ Show workflow backend response:', response);
          throw new Error(response?.data?.message || 'Failed to create show workflow');
        }
        
        console.log('✅ Created show workflow:', response.data);
      } else {
        // Create custom workflow
        const workflowSteps = enabledSteps.map((step, index) => ({
          title: step.title || `${formData.title} - Step ${index + 1}`,
          description: step.description || `Step ${index + 1} of ${formData.title}`,
          timing: step.daysOffset || 'day_of',
          time: step.time || '10am',
          enabled: true,
          priority: 'medium'
        }));
        
        const response = await postDataAPI('user/reminders/workflow', {
          workflowName,
          workflowDate,
          workflowType: 'custom',
          steps: workflowSteps,
          assignee_name: selectedMember?.name,
          assignee_role: selectedMember?.role,
          assignee_email: selectedMember?.email
        }, token);
        
        if (response?.status !== 201 && !response?.data?.success) {
          console.error('❌ Backend response:', response);
          throw new Error(response?.data?.message || 'Failed to create custom workflow');
        }
        
        console.log('✅ Created custom workflow:', response.data);
      }

      dispatch(setSuccessAlert(`${workflowName} workflow created successfully`));
      navigate('/flows');
      
    } catch (error) {
      console.error('Error creating workflow:', error);
      dispatch(setErrorAlert('Failed to create workflow'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>
              {workflowType === 'song_release' ? 'Song Release Workflow' : 
               workflowType === 'upcoming_show' ? 'Upcoming Show Workflow' : 
               workflowType === 'custom' ? 'Custom Workflow' : 
               'Create Workflow'}
            </span>
            <span 
              onClick={() => navigate('/flows')}
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            {/* Workflow Type Selection */}
            {!workflowType && (
              <div className="flex flex-col gap-4">
                <div
                  onClick={() => setWorkflowType('song_release')}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Song Release</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Pre-configured workflow for music releases with steps for distribution, pitching, and post-release tracking
                  </p>
                </div>

                <div
                  onClick={() => setWorkflowType('upcoming_show')}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Upcoming Show</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Pre-configured workflow for live shows with steps for booking, advance, travel, and settlement
                  </p>
                </div>

                <div
                  onClick={() => setWorkflowType('custom')}
                  className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all cursor-pointer"
                >
                  <h3 className="text-white text-[12px] font-normal mb-2">Custom Workflow</h3>
                  <p className="text-[rgba(255,255,255,0.7)] text-[11px] leading-[1.5]">
                    Build your own workflow from scratch with custom steps and timing
                  </p>
                </div>
              </div>
            )}

            {/* Song Release Workflow Form */}
            {workflowType === 'song_release' && (
              <div className="space-y-6 text-left">

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Song Name *
                  </label>
                  <input
                    type="text"
                    value={formData.songName}
                    onChange={(e) => handleInputChange('songName', e.target.value)}
                    placeholder="Enter song name..."
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30]"
                  />
                </div>

                <CustomCalendar
                  label="Release Date"
                  required
                  value={formData.releaseDate}
                  onChange={(date) => handleInputChange('releaseDate', date.split('T')[0])}
                  placeholder="Select release date..."
                />

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Add notes about this release..."
                    rows={3}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30] resize-none"
                  />
                </div>

                {/* Assign To Field */}
                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Assign To
                  </label>
                  <select
                    value={formData.assignee}
                    onChange={(e) => handleInputChange('assignee', e.target.value)}
                    disabled={loadingTeam}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 pr-8 text-white text-[12px] cursor-pointer disabled:opacity-50 outline-none focus:border-[#FF3B30]"
                    style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                  >
                    <option value="" className="bg-black">
                      {loadingTeam ? 'Loading team members...' : 'Select team member (optional)'}
                    </option>
                    {teamMembers.map(member => (
                      <option 
                        key={member.id} 
                        value={member.id} 
                        className="bg-black text-white"
                      >
                        {member.name} - {member.role}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Disable Follow-ups Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-white text-[12px] font-semibold mb-1">
                      Disable Pre-set Follow-ups
                    </label>
                    <p className="text-white text-[9px] opacity-50">
                      Turn off automatic follow-up reminders for each step
                    </p>
                  </div>
                  <button
                    onClick={() => setDisableFollowUps(!disableFollowUps)}
                    className={`ml-3 w-12 h-6 rounded-full transition-all ${
                      disableFollowUps ? 'bg-[#FF3B30]' : 'bg-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white transition-all ${
                        disableFollowUps ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <h3 className="text-white text-[13px] font-semibold mb-3">
                    Workflow Steps
                    <span className="text-[10px] opacity-50 ml-2 font-normal">
                      (Toggle to enable/disable)
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {steps.map((step, index) => (
                      <div
                        key={step.id}
                        className={`p-3 rounded-lg border transition-all ${
                          step.enabled
                            ? 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)]'
                            : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.05)] opacity-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-white text-[11px] font-medium mb-1">
                              Step {index + 1}/{steps.length}: {step.title}
                            </p>
                            <p className="text-white text-[9px] opacity-50">
                              {step.description}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleStepEnabled(step.id)}
                            className={`ml-3 w-10 h-5 rounded-full transition-all ${
                              step.enabled ? 'bg-[#FF3B30]' : 'bg-[rgba(255,255,255,0.1)]'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full bg-white transition-all ${
                                step.enabled ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Upcoming Show Workflow Form */}
            {workflowType === 'upcoming_show' && (
              <div className="space-y-6 text-left">

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Show Name/Venue *
                  </label>
                  <input
                    type="text"
                    value={formData.showName}
                    onChange={(e) => handleInputChange('showName', e.target.value)}
                    placeholder="Enter show name or venue..."
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30]"
                  />
                </div>

                <CustomCalendar
                  label="Performance Date"
                  required
                  value={formData.performanceDate}
                  onChange={(date) => handleInputChange('performanceDate', date.split('T')[0])}
                  placeholder="Select performance date..."
                  showTime={true}
                />

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Add notes about this show..."
                    rows={3}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30] resize-none"
                  />
                </div>

                {/* Assign To Field */}
                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Assign To
                  </label>
                  <select
                    value={formData.assignee}
                    onChange={(e) => handleInputChange('assignee', e.target.value)}
                    disabled={loadingTeam}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 pr-8 text-white text-[12px] cursor-pointer disabled:opacity-50 outline-none focus:border-[#FF3B30]"
                    style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                  >
                    <option value="" className="bg-black">
                      {loadingTeam ? 'Loading team members...' : 'Select team member (optional)'}
                    </option>
                    {teamMembers.map(member => (
                      <option 
                        key={member.id} 
                        value={member.id} 
                        className="bg-black text-white"
                      >
                        {member.name} - {member.role}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Disable Follow-ups Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-white text-[12px] font-semibold mb-1">
                      Disable Pre-set Follow-ups
                    </label>
                    <p className="text-white text-[9px] opacity-50">
                      Turn off automatic follow-up reminders for each step
                    </p>
                  </div>
                  <button
                    onClick={() => setDisableFollowUps(!disableFollowUps)}
                    className={`ml-3 w-12 h-6 rounded-full transition-all ${
                      disableFollowUps ? 'bg-[#FF3B30]' : 'bg-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-white transition-all ${
                        disableFollowUps ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <h3 className="text-white text-[13px] font-semibold mb-3">
                    Workflow Steps
                    <span className="text-[10px] opacity-50 ml-2 font-normal">
                      (Toggle to enable/disable)
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {steps.map((step, index) => (
                      <div
                        key={step.id}
                        className={`p-3 rounded-lg border transition-all ${
                          step.enabled
                            ? 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.1)]'
                            : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.05)] opacity-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-white text-[11px] font-medium mb-1">
                              Step {index + 1}/{steps.length}: {step.title}
                            </p>
                            <p className="text-white text-[9px] opacity-50">
                              {step.description}
                            </p>
                          </div>
                          <button
                            onClick={() => toggleStepEnabled(step.id)}
                            className={`ml-3 w-10 h-5 rounded-full transition-all ${
                              step.enabled ? 'bg-[#FF3B30]' : 'bg-[rgba(255,255,255,0.1)]'
                            }`}
                          >
                            <div
                              className={`w-4 h-4 rounded-full bg-white transition-all ${
                                step.enabled ? 'translate-x-5' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Workflow Form */}
            {workflowType === 'custom' && (
              <div className="space-y-6 text-left">

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Workflow Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter workflow title..."
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30]"
                  />
                </div>

                <CustomCalendar
                  label="Target Date"
                  required
                  value={formData.releaseDate}
                  onChange={(date) => handleInputChange('releaseDate', date.split('T')[0])}
                  placeholder="Select target date..."
                />

                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Add notes about this workflow..."
                    rows={3}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30] resize-none"
                  />
                </div>

                {/* Assign To Field */}
                <div>
                  <label className="block text-white text-[12px] font-semibold mb-2">
                    Assign To
                  </label>
                  <select
                    value={formData.assignee}
                    onChange={(e) => handleInputChange('assignee', e.target.value)}
                    disabled={loadingTeam}
                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 pr-8 text-white text-[12px] cursor-pointer disabled:opacity-50 outline-none focus:border-[#FF3B30]"
                    style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                  >
                    <option value="" className="bg-black">
                      {loadingTeam ? 'Loading team members...' : 'Select team member (optional)'}
                    </option>
                    {teamMembers.map(member => (
                      <option 
                        key={member.id} 
                        value={member.id} 
                        className="bg-black text-white"
                      >
                        {member.name} - {member.role}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-white text-[13px] font-semibold">
                      Custom Steps
                    </h3>
                    <button
                      onClick={addCustomStep}
                      className="text-[#FF3B30] text-[11px] font-medium hover:opacity-70"
                    >
                      + Add Step
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {steps.map((step, index) => (
                      <div
                        key={step.id}
                        className="p-4 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white text-[11px] font-medium">
                            Step {index + 1}
                          </span>
                          {steps.length > 1 && (
                            <button
                              onClick={() => removeCustomStep(step.id)}
                              className="text-red-500 text-[10px] hover:opacity-70"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={step.title}
                            onChange={(e) => updateCustomStep(step.id, 'title', e.target.value)}
                            placeholder="Step title..."
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30]"
                          />
                          
                          <textarea
                            value={step.description}
                            onChange={(e) => updateCustomStep(step.id, 'description', e.target.value)}
                            placeholder="Step description..."
                            rows={2}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-2 text-white text-[11px] placeholder-[rgba(255,255,255,0.5)] outline-none focus:border-[#FF3B30] resize-none"
                          />
                          
                          <div>
                            <label className="block text-white text-[10px] opacity-50 mb-1">
                              Days before target date
                            </label>
                            <input
                              type="number"
                              value={step.daysOffset}
                              onChange={(e) => updateCustomStep(step.id, 'daysOffset', e.target.value)}
                              min="0"
                              className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-2 text-white text-[11px] outline-none focus:border-[#FF3B30]"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Create Button */}
            {workflowType && (
              <button
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full bg-[#FF3B30] text-white text-[12px] font-semibold py-4 rounded-lg hover:bg-[rgba(255,59,48,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-8"
              >
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creating Workflow...
                  </>
                ) : (
                  'Create Workflow'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default CreateCustomWorkflow;

